import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, ArrowLeft, ShieldCheck, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

export function destinoDepoisDoLogin(user) {
  if (user?.precisa_trocar_senha || user?.precisa_2fa) return "/conta?aba=seguranca";
  if (user && !user.tem_acesso) return "/assinatura";
  return "/";
}

export default function LoginPage() {
  const { login, login2fa, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | codigo | forgot | enviado
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [desafio, setDesafio] = useState("");
  const [codigo, setCodigo] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");

  const entrar = (user) => {
    toast.success("Login realizado com sucesso!");
    navigate(destinoDepoisDoLogin(user), { replace: true });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res.requer_2fa) {
        setDesafio(res.desafio);
        setCodigo("");
        setMode("codigo");
      } else {
        entrar(res);
      }
    } catch (err) {
      if (!err.response) {
        toast.error("Servidor iniciando... Tente novamente em alguns segundos.");
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodigo = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login2fa(desafio, codigo);
      entrar(res);
    } catch (err) {
      const detalhe = formatApiError(err.response?.data?.detail);
      toast.error(detalhe);
      if (err.response?.status === 401 && detalhe.includes("Entre de novo")) {
        setMode("login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setMode("enviado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const titulos = {
    login: "Entrar",
    codigo: "Verificação em duas etapas",
    forgot: "Recuperar senha",
    enviado: "Confira seu e-mail",
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <h1 className="text-3xl font-bold text-teal-700 dark:text-teal-400" style={{ fontFamily: 'Manrope' }}>
              Aesthetica
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Pricing Pro</p>
          </Link>
        </div>

        <Card className="border-slate-200 dark:border-slate-800 shadow-lg" data-testid="auth-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-center text-slate-900 dark:text-white">{titulos[mode]}</CardTitle>
          </CardHeader>
          <CardContent>
            {mode === "login" && (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com" required autoComplete="email" data-testid="login-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Senha</Label>
                  <div className="relative">
                    <Input id="password" type={showPassword ? "text" : "password"} value={password}
                      onChange={(e) => setPassword(e.target.value)} placeholder="Sua senha" required
                      autoComplete="current-password" data-testid="login-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      aria-label={showPassword ? "Esconder senha" : "Mostrar senha"}>
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={loading} data-testid="login-submit-btn">
                  {loading ? "Entrando..." : "Entrar"}
                </Button>
                <div className="text-center">
                  <button type="button" onClick={() => { setForgotEmail(email); setMode("forgot"); }}
                    className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400" data-testid="forgot-password-link">
                    Esqueceu a senha?
                  </button>
                </div>
                <div className="border-t border-slate-200 dark:border-slate-700 pt-4 text-center">
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">Ainda não é assinante?</p>
                  <Button type="button" variant="outline"
                    className="w-full border-teal-600 text-teal-600 hover:bg-teal-50 dark:border-teal-400 dark:text-teal-400"
                    onClick={() => { window.location.href = "/#planos"; }} data-testid="buy-now-btn">
                    Ver planos, a partir de R$ 25/mês
                  </Button>
                </div>
              </form>
            )}

            {mode === "codigo" && (
              <form onSubmit={handleCodigo} className="space-y-4">
                <div className="flex items-start gap-3 rounded-lg bg-teal-50 dark:bg-teal-900/20 p-3 text-sm text-teal-800 dark:text-teal-200">
                  <ShieldCheck className="h-5 w-5 shrink-0" />
                  Abra o aplicativo autenticador no celular e digite o código de 6 números do Aesthetica Pricing.
                </div>
                <div className="space-y-2">
                  <Label htmlFor="codigo">Código</Label>
                  <Input id="codigo" value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder="000000"
                    inputMode="numeric" autoComplete="one-time-code" autoFocus required maxLength={9}
                    className="text-center text-2xl tracking-[0.4em] font-mono" data-testid="login-2fa-input" />
                  <p className="text-xs text-slate-500">Perdeu o celular? Digite um dos códigos de reserva (formato XXXX-XXXX).</p>
                  <p className="text-xs text-slate-500">Depois do código, este navegador fica lembrado por 30 dias e não pede o código de novo.</p>
                </div>
                <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={loading} data-testid="login-2fa-submit">
                  {loading ? "Conferindo..." : "Confirmar"}
                </Button>
                <button type="button" onClick={() => setMode("login")}
                  className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400">
                  <ArrowLeft className="w-3 h-3" /> Voltar
                </button>
              </form>
            )}

            {mode === "forgot" && (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Digite o e-mail da sua conta. Vamos enviar um link para você criar uma senha nova.
                </p>
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">E-mail</Label>
                  <Input id="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="seu@email.com" required data-testid="forgot-email-input" />
                </div>
                <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={loading} data-testid="forgot-submit-btn">
                  {loading ? "Enviando..." : "Enviar link"}
                </Button>
                <button type="button" onClick={() => setMode("login")}
                  className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400"
                  data-testid="back-to-login-from-forgot">
                  <ArrowLeft className="w-3 h-3" /> Voltar ao login
                </button>
              </form>
            )}

            {mode === "enviado" && (
              <div className="space-y-4 text-center">
                <MailCheck className="mx-auto h-12 w-12 text-teal-600" />
                <p className="text-slate-600 dark:text-slate-300">
                  Se <strong>{forgotEmail}</strong> estiver cadastrado, o link chega em alguns minutos. Ele vale por 1 hora.
                </p>
                <p className="text-sm text-slate-500">Não chegou? Confira a caixa de spam ou promoções.</p>
                <Button variant="outline" className="w-full" onClick={() => setMode("login")}>Voltar ao login</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
